import React, { createContext, useContext, useEffect, useState } from 'react';
import { connect } from 'dva';
import { StoreState } from '@src/interfaces';

interface Locale {
  languageId: string;
  locale: string;
  name: string;
  icon: string;
}

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'app_locale';

// Helper functions for localStorage
export const getStoredLocale = (): Locale | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading locale from localStorage:', error);
  }
  return null;
};

export const saveLocaleToStorage = (locale: Locale): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(locale));
  } catch (error) {
    console.error('Error saving locale to localStorage:', error);
  }
};

interface LanguageProviderProps {
  children: React.ReactNode;
  locale: Locale;
  dispatch?: any;
}

const LanguageProviderComponent: React.FC<LanguageProviderProps> = ({
  children,
  locale,
  dispatch,
}) => {
  const [currentLocale, setCurrentLocale] = useState<Locale>(locale);

  useEffect(() => {
    setCurrentLocale(locale);
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    setCurrentLocale(newLocale);
    saveLocaleToStorage(newLocale);
    if (dispatch) {
      dispatch({
        type: 'settings/switchLanguage',
        payload: newLocale,
      });
    }
  };

  return (
    <LanguageContext.Provider value={{ locale: currentLocale, setLocale }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Connected component that reads from Redux
const ConnectedLanguageProvider = connect(({ settings }: StoreState) => ({
  locale: settings.locale,
}))(LanguageProviderComponent);

// Hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default ConnectedLanguageProvider;

